/**
 * Le mouvement de la fiche, quand elle se retourne sur ses deux parcours.
 *
 * ## Un seul geste, et non six animations en parallèle
 *
 * La fiche ne change pas d'écran : elle se réorganise. Ce qui fait lire un
 * mouvement plutôt qu'une succession tient à trois choses, et elles sont toutes
 * dans ce fichier.
 *
 * **Une vague qui part du doigt.** Les blocs s'effacent du bas vers le haut, en
 * commençant par celui qu'on vient de toucher. Partir du haut de l'écran ferait
 * naître le mouvement là où le regard n'est pas.
 *
 * **Un seul intervalle pour tout ce qui se déplace.** La titraille qui monte,
 * les cinq boîtes qui se rétractent, les espaceurs qui se ferment et le
 * surtitre qui remonte partagent la même fenêtre et la même courbe. Sept
 * propriétés qui bougent ensemble se lisent comme une masse ; échelonnées,
 * elles se liraient comme sept objets.
 *
 * **Un recouvrement.** La rétraction commence cent soixante-dix millisecondes
 * avant que la disparition ait fini. Bout à bout, on lirait deux animations.
 *
 * ## Le retour est plus sec que l'aller
 *
 * L'aller est une cérémonie, le retour est un repli : personne ne veut attendre
 * sept dixièmes de seconde pour revenir sur ses pas. Chaque bloc porte donc
 * deux temps, et c'est **l'état d'arrivée qui impose le sien** : une transition
 * déclarée sur la valeur cible est celle qui joue. La cascade se retourne aussi,
 * du haut vers le bas, parce que la vague redescend.
 */

/**
 * Ce qui se déplace.
 *
 * L'`easeOutQuart` de la maison, celle de la transition de progression : un
 * départ franc et une arrivée qui se pose. C'est la courbe de tout ce qui va
 * d'un endroit à un autre.
 */
export const EASE_MOVE = "cubic-bezier(0.22, 1, 0.36, 1)";

/**
 * Ce qui arrive.
 *
 * La courbe d'entrée du produit, celle des cartes de briefing, des toasts et
 * des indices d'import. Rien de nouveau n'entre autrement.
 */
export const EASE_OUT = "cubic-bezier(0.16, 1, 0.3, 1)";

/** Ce qui part : la symétrique, celle de la chute des notes à l'ouverture. */
export const EASE_IN = "cubic-bezier(0.32, 0, 0.67, 0)";

/** Un temps de la chorégraphie : quand il commence, et combien il dure. */
export type Beat = {
  readonly delay: number;
  readonly ms: number;
};

/**
 * Ce qu'un objet fait à l'aller, et ce qu'il fait au retour.
 *
 * Le sens est celui de **l'écran**, pas celui de l'objet : `out` est le trajet
 * vers le choix des parcours, quoi que l'objet y fasse. Les cinq blocs de la
 * fiche s'en vont à l'aller, celui du choix arrive, et les deux prennent la
 * cadence de l'aller.
 */
export type Move = {
  /** Vers le choix des parcours. */
  readonly out: Beat;
  /** Vers la fiche. */
  readonly back: Beat;
};

/** Le temps que ce mouvement prend, dans le sens où l'écran va. */
export function pick(move: Move, toProgram: boolean): Beat {
  return toProgram ? move.out : move.back;
}

/**
 * Le grand mouvement : tout ce qui se déplace, ensemble.
 *
 * Il démarre après le début de la vague et finit après elle. C'est le
 * recouvrement.
 */
export const SHIFT: Move = {
  out: { delay: 180, ms: 440 },
  back: { delay: 0, ms: 240 },
};

/**
 * La vague de disparition, bloc par bloc, du bas vers le haut.
 *
 * Au retour, la même liste dans l'autre sens et sans les écarts fins : trente
 * millisecondes d'un bloc au suivant suffisent à ce que ça ne clignote pas d'un
 * bloc.
 */
export const FADE = {
  /** La ligne d'action : c'est elle qu'on vient de toucher. */
  action: { out: { delay: 0, ms: 90 }, back: { delay: 170, ms: 200 } },
  /** La progression d'accords, et son « Explorer ». */
  progression: { out: { delay: 60, ms: 180 }, back: { delay: 140, ms: 200 } },
  /** Les quatre respirations musicales. */
  stats: { out: { delay: 100, ms: 150 }, back: { delay: 110, ms: 200 } },
  /** La ligne de contexte du hero, sous le compositeur. */
  context: { out: { delay: 120, ms: 160 }, back: { delay: 80, ms: 200 } },
  /**
   * La difficulté par mesure, seule à sortir par le bas.
   *
   * C'est le seul bloc situé sous le point de clic, donc le seul que la vague
   * pousse vers le bas. Cet écartement est ce qui donne au mouvement son
   * amplitude.
   */
  difficulty: { out: { delay: 140, ms: 260 }, back: { delay: 200, ms: 200 } },
} as const satisfies Record<string, Move>;

/**
 * De combien la quatrième respiration part avant la première.
 *
 * Les stats s'éteignent de droite à gauche, donc la tonalité, qui est la plus à
 * gauche, part en dernier. Ce n'est pas une coquetterie : elle est la seule des
 * quatre qui survive, en se reposant à côté du compositeur. Les deux temps se
 * recouvrent de quatre-vingts millisecondes, et l'oeil voit alors une donnée
 * changer de place au lieu de deux évènements.
 *
 * Au retour, les quatre reviennent ensemble : le repli n'a rien à raconter.
 */
export const STAT_STAGGER_MS = 32;

/** L'arrivée du choix, une fois la place faite. */
export const ARRIVE = {
  /** Le surtitre change de mot, à gauche. */
  overline: { delay: 200, ms: 200 },
  /** Le geste de retour prend la place des chiffres, à droite. */
  back: { delay: 240, ms: 200 },
  /** La tonalité se repose à côté du compositeur. */
  key: { delay: 260, ms: 180 },
  /** Le filet vertical s'allume sur l'axe. */
  rule: { delay: 300, ms: 320 },
  /** Les deux colonnes, puis le pied. */
  left: 340,
  right: 400,
  foot: 440,
} as const;

/**
 * L'aurore, qui finit la dernière.
 *
 * Le contenu s'ancre en haut et le bas se vide : la lumière suit. Elle met
 * quatre-vingts millisecondes de plus que tout le reste, et c'est la traîne du
 * geste — l'oeil a fini de lire, la lumière finit de se poser.
 */
export const AURORA: Move = {
  out: { delay: 180, ms: 720 },
  back: { delay: 0, ms: 420 },
};

/** Une transition CSS, écrite depuis un temps de la chorégraphie. */
export function ease(property: string, beat: Beat, curve: string): string {
  return `${property} ${beat.ms}ms ${beat.delay}ms ${curve}`;
}
